import { alpha } from '@mui/material/styles';
import { JLR_PLANT } from '../../constants';

export default function TextField(theme) {
  const isJlr = theme.plantName === JLR_PLANT;
  const isDarkMode = theme.palette.mode === 'dark';

  const jlrTextFieldStyles = {
    // Outlined variant (primary)
    outlined: {
      '& .MuiOutlinedInput-root': {
        fontFamily: "'Inter', sans-serif",
        fontSize: '1rem',
        borderRadius: '12px',
        background: isDarkMode 
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(8px)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        
        '& fieldset': {
          borderColor: isDarkMode 
            ? 'rgba(255, 255, 255, 0.2)'
            : 'rgba(45, 74, 45, 0.2)',
          borderWidth: '1px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        
        '&:hover fieldset': {
          borderColor: isDarkMode 
            ? 'rgba(0, 102, 204, 0.5)'
            : 'rgba(45, 74, 45, 0.5)',
          borderWidth: '2px',
        },
        
        '&.Mui-focused fieldset': {
          borderColor: isDarkMode 
            ? '#0066cc'
            : '#2d4a2d',
          borderWidth: '2px',
          boxShadow: isDarkMode 
            ? '0 0 0 3px rgba(0, 102, 204, 0.1)'
            : '0 0 0 3px rgba(45, 74, 45, 0.1)',
        },
        
        '&.Mui-error fieldset': {
          borderColor: '#dc3545',
          '&:hover': {
            borderColor: '#dc3545',
          },
        },
        
        '&.Mui-disabled fieldset': {
          borderColor: isDarkMode 
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(45, 74, 45, 0.1)',
          background: isDarkMode 
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(0, 0, 0, 0.02)',
        },
      },
      
      '& .MuiInputBase-input': {
        color: isDarkMode ? '#ffffff' : '#2d4a2d',
        padding: '14px 16px',
        '&::placeholder': {
          color: isDarkMode 
            ? 'rgba(255, 255, 255, 0.5)'
            : 'rgba(45, 74, 45, 0.5)',
          opacity: 1,
        },
      },
    },

    // Filled variant
    filled: {
      '& .MuiFilledInput-root': {
        fontFamily: "'Inter', sans-serif",
        borderRadius: '12px 12px 0 0',
        background: isDarkMode 
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(45, 74, 45, 0.04)',
        border: 'none',
        
        '&:hover': {
          background: isDarkMode 
            ? 'rgba(255, 255, 255, 0.12)'
            : 'rgba(45, 74, 45, 0.08)',
        },
        
        '&.Mui-focused': {
          background: isDarkMode 
            ? 'rgba(255, 255, 255, 0.16)'
            : 'rgba(45, 74, 45, 0.12)',
        },
        
        '&:before': {
          borderBottom: `2px solid ${isDarkMode 
            ? 'rgba(255, 255, 255, 0.2)'
            : 'rgba(45, 74, 45, 0.2)'}`,
        },
        
        '&:after': {
          borderBottom: `2px solid ${isDarkMode 
            ? '#0066cc'
            : '#2d4a2d'}`,
        },
      },
      
      '& .MuiInputBase-input': {
        color: isDarkMode ? '#ffffff' : '#2d4a2d',
        padding: '16px 12px 8px',
      },
    },

    // Standard variant
    standard: {
      '& .MuiInput-root': {
        fontFamily: "'Inter', sans-serif",
        
        '&:before': {
          borderBottom: `1px solid ${isDarkMode 
            ? 'rgba(255, 255, 255, 0.2)'
            : 'rgba(45, 74, 45, 0.2)'}`,
        },
        
        '&:after': {
          borderBottom: `2px solid ${isDarkMode 
            ? '#0066cc'
            : '#2d4a2d'}`,
        },
        
        '&:hover:not(.Mui-disabled):before': {
          borderBottom: `2px solid ${isDarkMode 
            ? 'rgba(255, 255, 255, 0.4)'
            : 'rgba(45, 74, 45, 0.4)'}`,
        },
      },
      
      '& .MuiInputBase-input': {
        color: isDarkMode ? '#ffffff' : '#2d4a2d',
        padding: '8px 0',
      },
    },
  };

  const standardTextFieldStyles = {
    outlined: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
      },
    },
    filled: {},
    standard: {},
  };

  const styles = isJlr ? jlrTextFieldStyles : standardTextFieldStyles;

  return {
    MuiTextField: {
      variants: [
        {
          props: { variant: 'outlined' },
          style: styles.outlined,
        },
        {
          props: { variant: 'filled' },
          style: styles.filled,
        },
        {
          props: { variant: 'standard' },
          style: styles.standard,
        },
      ],
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: isJlr ? "'Inter', sans-serif" : theme.typography.fontFamily,
          fontWeight: isJlr ? 500 : 400,
          color: isJlr ? (isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(45, 74, 45, 0.7)') : theme.palette.text.secondary,
          
          '&.Mui-focused': {
            color: isJlr ? (isDarkMode ? '#0066cc' : '#2d4a2d') : theme.palette.primary.main,
          },
          
          '&.Mui-error': {
            color: '#dc3545',
          },
        },
        
        outlined: {
          transform: 'translate(16px, 16px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(16px, -6px) scale(0.75)',
          },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: isJlr ? "'Inter', sans-serif" : theme.typography.fontFamily,
          fontSize: isJlr ? '0.75rem' : '0.75rem',
          marginTop: isJlr ? '8px' : '4px',
          marginLeft: isJlr ? '16px' : '14px',
          color: isJlr ? (isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(45, 74, 45, 0.6)') : theme.palette.text.secondary,
          
          '&.Mui-error': {
            color: '#dc3545',
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: isJlr ? "'Inter', sans-serif" : theme.typography.fontFamily,
        },
        
        icon: {
          color: isJlr ? (isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(45, 74, 45, 0.7)') : theme.palette.text.secondary,
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: isJlr ? '12px' : '8px',
          background: isJlr 
            ? (isDarkMode 
              ? 'linear-gradient(135deg, #1a1d20, #2d3136)'
              : 'linear-gradient(135deg, #ffffff, #fafbfc)')
            : theme.palette.background.paper,
          border: isJlr 
            ? `1px solid ${isDarkMode 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(45, 74, 45, 0.1)'}`
            : `1px solid ${theme.palette.divider}`,
          boxShadow: isJlr 
            ? (isDarkMode 
              ? '0 16px 40px rgba(0, 0, 0, 0.3)'
              : '0 16px 40px rgba(45, 74, 45, 0.12)')
            : theme.shadows[8],
        },
        
        option: {
          fontFamily: isJlr ? "'Inter', sans-serif" : theme.typography.fontFamily,
          padding: isJlr ? '12px 16px' : '8px 12px',
          
          '&:hover': {
            background: isJlr 
              ? (isDarkMode 
                ? 'rgba(0, 102, 204, 0.1)' 
                : 'rgba(45, 74, 45, 0.08)')
              : alpha(theme.palette.primary.main, 0.04),
          },
          
          '&.Mui-focused': {
            background: isJlr 
              ? (isDarkMode 
                ? 'rgba(0, 102, 204, 0.2)' 
                : 'rgba(45, 74, 45, 0.12)')
              : alpha(theme.palette.primary.main, 0.08),
          },
        },
      },
    },
  };
}
